import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Import useAuth directly - require() doesn't work in Vite/ESM
import { useAuth } from '@/contexts/AuthContext';

const useSafeAuth = () => {
  try {
    return useAuth();
  } catch {
    return { isAdmin: false };
  }
};

interface ClickTrackingOptions {
  pageUrl: string;
  matchId?: string;
  streamId?: string;
  affiliateCode?: string;
}

interface VisitorInfo {
  ip_address: string | null;
  country: string | null;
  browser: string;
  device_type: string;
  visitor_key: string;
  referrer: string | null;
}

// Store visitor info in memory to avoid repeated API calls
let cachedVisitorInfo: VisitorInfo | null = null;
let visitorInfoPromise: Promise<VisitorInfo> | null = null;

// Session tracking - only count one click per session entry
const SESSION_KEY = 'gs_session_click_tracked';

// Get or create session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('gs_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('gs_session_id', sessionId);
  }
  return sessionId;
};

// Check if click already tracked for this session
const isClickTrackedForSession = (): boolean => {
  return sessionStorage.getItem(SESSION_KEY) === 'true';
};

// Mark click as tracked for this session
const markClickTrackedForSession = (): void => {
  sessionStorage.setItem(SESSION_KEY, 'true');
};

// Get browser name from user agent
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
};

// Get device type from user agent
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  return /Mobile|Android|iPhone|iPad/i.test(ua) ? 'mobile' : 'desktop';
};

// Get visitor info with caching
const getVisitorInfo = async (): Promise<VisitorInfo> => {
  if (cachedVisitorInfo) {
    return cachedVisitorInfo;
  }

  if (visitorInfoPromise) {
    return visitorInfoPromise;
  }

  visitorInfoPromise = (async () => {
    const browser = getBrowser();
    const device_type = getDeviceType();
    let ip_address: string | null = null;
    let country: string | null = null;

    try {
      const ipRes = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipRes.json();
      ip_address = ipData.ip;

      const geoRes = await fetch(`https://ipapi.co/${ip_address}/json/`);
      const geoData = await geoRes.json();
      country = geoData.country_code || null;
    } catch (e) {
      console.log('Could not fetch IP/geo info');
    }

    const visitor_key = `${ip_address || 'unknown'}-${device_type}-${browser}`;

    cachedVisitorInfo = {
      ip_address,
      country,
      browser,
      device_type,
      visitor_key,
      referrer: document.referrer || null,
    };

    return cachedVisitorInfo;
  })();

  return visitorInfoPromise;
};

// Check if visitor entered via affiliate link or campaign link
const getAffiliateFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  // Check for campaign links (?camp=), regular affiliate links (?ref= or ?aff=)
  return urlParams.get('camp') || urlParams.get('ref') || urlParams.get('aff') || null;
};

// Store affiliate code in session storage for attribution
const storeAffiliateCode = (code: string) => {
  try {
    sessionStorage.setItem('affiliate_code', code);
  } catch (e) {
    console.log('Could not store affiliate code');
  }
};

// Get stored affiliate code
const getStoredAffiliateCode = (): string | null => {
  try {
    return sessionStorage.getItem('affiliate_code');
  } catch (e) {
    return null;
  }
};

/**
 * Track a click event - SESSION-BASED: Only counts one click per session entry
 * @param options - Click tracking options
 * @param isAdmin - If true, skip click tracking (admin clicks don't count)
 */
export const trackClick = async (options: ClickTrackingOptions, isAdmin: boolean = false): Promise<void> => {
  // Skip tracking for admin users
  if (isAdmin) {
    console.log('Admin click - skipping tracking');
    return;
  }

  // SESSION-BASED: Only track once per session (site entry)
  if (isClickTrackedForSession()) {
    console.log('Click already tracked for this session - skipping');
    return;
  }

  try {
    const visitorInfo = await getVisitorInfo();
    const sessionId = getSessionId();
    
    // Check for affiliate code in URL or stored in session
    const urlAffiliateCode = getAffiliateFromUrl();
    if (urlAffiliateCode) {
      storeAffiliateCode(urlAffiliateCode);
    }
    
    const affiliateCode = options.affiliateCode || urlAffiliateCode || getStoredAffiliateCode();
    
    // Resolve affiliate link ID if we have an affiliate code
    let affiliateLinkId: string | null = null;
    
    if (affiliateCode) {
      const { data: resolvedId, error: resolveError } = await supabase
        .rpc('resolve_affiliate_code', { _code: affiliateCode });
      
      if (!resolveError && resolvedId) {
        affiliateLinkId = resolvedId;
      }
    }

    // INSERT click record (classification handled by database trigger)
    const { error: insertError } = await supabase
      .from('click_logs')
      .insert({
        affiliate_link_id: affiliateLinkId,
        ip_address: visitorInfo.ip_address,
        country: visitorInfo.country,
        browser: visitorInfo.browser,
        device_type: visitorInfo.device_type,
        session_id: sessionId,
        match_id: options.matchId || null,
        stream_id: options.streamId || null,
        page_url: options.pageUrl,
        referrer: visitorInfo.referrer,
        visitor_key: visitorInfo.visitor_key,
      });

    if (insertError) {
      console.error('Error inserting click:', insertError);
      return;
    }

    // Also update activity log to refresh online presence (for guest tracking)
    // This allows detecting recently active users even if they just arrived
    const now = new Date().toISOString();
    
    // Try to find existing activity by session and update heartbeat
    const { data: existingActivity } = await supabase
      .from('user_activity_logs')
      .select('id')
      .eq('session_id', sessionId)
      .limit(1)
      .maybeSingle();

    if (existingActivity) {
      await supabase
        .from('user_activity_logs')
        .update({ 
          last_heartbeat: now,
          page_visited: options.pageUrl,
          ip_address: visitorInfo.ip_address,
          country: visitorInfo.country,
          browser: visitorInfo.browser,
          device_type: visitorInfo.device_type,
        })
        .eq('id', existingActivity.id);
    } else {
      // Create new activity log entry if none exists
      await supabase
        .from('user_activity_logs')
        .insert({
          session_id: sessionId,
          page_visited: options.pageUrl,
          ip_address: visitorInfo.ip_address,
          country: visitorInfo.country,
          browser: visitorInfo.browser,
          device_type: visitorInfo.device_type,
          referrer: visitorInfo.referrer,
          user_role: 'guest',
          last_heartbeat: now,
          affiliate_link_id: affiliateLinkId,
        });
    }

    // Mark as tracked for this session
    markClickTrackedForSession();
    console.log(`Click tracked on ${options.pageUrl} (session: ${sessionId})`);

  } catch (e) {
    console.error('Error in trackClick:', e);
  }
};

/**
 * Hook for automatic click tracking on page load
 * Only tracks ONCE per session entry (browser session)
 */
export function useClickTracking(options: ClickTrackingOptions) {
  const trackedRef = useRef(false);
  const authData = useSafeAuth();
  const isAdmin = authData?.isAdmin || false;

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    trackClick(options, isAdmin);
  }, [options.pageUrl, options.matchId, options.streamId, options.affiliateCode, isAdmin]);
}

/**
 * Hook for tracking specific click events
 */
export function useClickTracker() {
  const authData = useSafeAuth();
  const isAdmin = authData?.isAdmin || false;

  const trackMatchClick = useCallback(async (matchId: string, pageUrl: string) => {
    await trackClick({ pageUrl, matchId }, isAdmin);
  }, [isAdmin]);

  const trackStreamClick = useCallback(async (streamId: string, matchId: string, pageUrl: string) => {
    await trackClick({ pageUrl, matchId, streamId }, isAdmin);
  }, [isAdmin]);

  const trackNavigationClick = useCallback(async (pageUrl: string) => {
    await trackClick({ pageUrl }, isAdmin);
  }, [isAdmin]);

  const trackAffiliateClick = useCallback(async (affiliateCode: string, pageUrl: string) => {
    await trackClick({ pageUrl, affiliateCode }, isAdmin);
  }, [isAdmin]);

  // Track any activity to update presence (doesn't create click log)
  const updatePresence = useCallback(async (pageUrl: string) => {
    if (isAdmin) return;
    
    try {
      const visitorInfo = await getVisitorInfo();
      const sessionId = getSessionId();
      const now = new Date().toISOString();
      
      // Update existing activity or create new one
      const { data: existingActivity } = await supabase
        .from('user_activity_logs')
        .select('id')
        .eq('session_id', sessionId)
        .limit(1)
        .maybeSingle();

      if (existingActivity) {
        await supabase
          .from('user_activity_logs')
          .update({ 
            last_heartbeat: now,
            page_visited: pageUrl,
          })
          .eq('id', existingActivity.id);
      } else {
        await supabase
          .from('user_activity_logs')
          .insert({
            session_id: sessionId,
            page_visited: pageUrl,
            ip_address: visitorInfo.ip_address,
            country: visitorInfo.country,
            browser: visitorInfo.browser,
            device_type: visitorInfo.device_type,
            referrer: visitorInfo.referrer,
            user_role: 'guest',
            last_heartbeat: now,
          });
      }
    } catch (e) {
      console.error('Error updating presence:', e);
    }
  }, [isAdmin]);

  return {
    trackMatchClick,
    trackStreamClick,
    trackNavigationClick,
    trackAffiliateClick,
    updatePresence,
  };
}
